import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import PasswordStrengthBar from 'react-password-strength-bar';
import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile
} from "firebase/auth";
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Alert,
    CircularProgress,
    Snackbar,
    Paper,
    Avatar,
    Fade,
    useTheme,
    useMediaQuery,
    Grid,
    Chip
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SchoolIcon from '@mui/icons-material/School';
import LinkIcon from '@mui/icons-material/Link';
import { useLanguage } from "./LanguageContext";

// Configuration Firebase
const firebaseConfig = {
    apiKey: "AIzaSyCZd4Sh0iGzcKTa5BPKaW87Q8Yqy0dSoi8",
    authDomain: "classcapture-4fc8f.firebaseapp.com",
    projectId: "classcapture-4fc8f",
    storageBucket: "classcapture-4fc8f.appspot.com",
    messagingSenderId: "302532812957",
    appId: "1:302532812957:web:970d7c23f2157a2ad3f474",
    measurementId: "G-V0B1KYV8XW"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const Authorisation = () => {
    const {t} = useLanguage();
    const [isSignUp, setIsSignUp] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/dashboard";

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: ""
    });
    const [passwordVisible, setPasswordVisible] = useState(false);
    const emailInputRef = useRef(null);
    const firstNameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        document.title = isSignUp
            ? "Sign Up | ClassCapture"
            : "Sign In | ClassCapture";
    }, [isSignUp]);

    useEffect(() => {
        if (window.location.pathname === "/register") setIsSignUp(true);
        if (window.location.pathname === "/login") setIsSignUp(false);
    }, []);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (isSignUp) {
                if (!formData.firstName.trim()) {
                    setError("Le prénom est requis");
                    firstNameInputRef.current.focus();
                    return;
                }
                if (!formData.lastName.trim()) {
                    setError("Le nom de famille est requis");
                    lastNameInputRef.current.focus();
                    return;
                }
                const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
                if (!emailRegex.test(formData.email)) {
                    setError("Email invalide");
                    emailInputRef.current.focus();
                    return;
                }
                // Création utilisateur
                const userCredential = await createUserWithEmailAndPassword(
                    auth,
                    formData.email,
                    formData.password
                );
                const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
                await updateProfile(userCredential.user, { displayName: fullName });
                const userDoc = {
                    uid: userCredential.user.uid,
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    email: formData.email,
                    cid: [],
                    createdAt: new Date().toISOString()
                };
                const db = getFirestore(app);
                await setDoc(doc(db, "users", userCredential.user.uid), userDoc);
                setSnackbar({
                    open: true,
                    message: "Inscription réussie !",
                    severity: "success"
                });
            } else {
                // Connexion
                await signInWithEmailAndPassword(
                    auth,
                    formData.email,
                    formData.password
                );
                setSnackbar({
                    open: true,
                    message: "Connexion réussie !",
                    severity: "success"
                });
            }

            // redirection vers la page d'origine ou dashboard
            navigate(from, { replace: true });
        } catch (err) {
            setError(
                isSignUp
                    ? "Erreur lors de l'inscription : " + err.message
                    : "Identifiants invalides : " + err.message
            );
            emailInputRef.current.focus();
        } finally {
            setLoading(false);
        }
    };

    const toggleForm = () => {
        setIsSignUp(!isSignUp);
        // Conserver `from` dans le state lors du switch
        navigate(isSignUp ? "/login" : "/register", {
            state: { from }
        });
        setFormData({ firstName: "", lastName: "", email: "", password: "" });
        setError(null);
    };

    const togglePasswordVisibility = () =>
        setPasswordVisible((vis) => !vis);

    const handleCloseSnackbar = () =>
        setSnackbar({ ...snackbar, open: false });

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
    };

    return (
        <Container component="main" maxWidth="md" sx={{ mt: 8, mb: 8 }}>
            <Fade in timeout={800}>
                <Paper
                    elevation={8}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        minHeight: '500px'
                    }}
                >
                    {!isMobile && (
                        <Box
                            sx={{
                                flex: '0 0 40%',
                                ...gradientBackground,
                                color: 'black',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                p: 4
                            }}
                        >
                            <SchoolIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
                                ClassCapture
                            </Typography>
                            <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                                Knowledge captured, success assured
                            </Typography>
                        </Box>
                    )}
                    <Box
                        sx={{
                            flex: '1 1 60%',
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
                                <SchoolIcon sx={{ fontSize: 32, mr: 1, color: theme.palette.primary.main }} />
                                <Typography variant="h5" component="h1" color="primary" fontWeight={700}>
                                    ClassCapture
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <Avatar
                                sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56, mb: 1 }}
                            >
                                {isSignUp ? <PersonAddOutlinedIcon /> : <LockOutlinedIcon />}
                            </Avatar>
                            <Typography variant="h5" component="h2">
                                {isSignUp ? t.createAccount : t.connexion}
                            </Typography>
                        </Box>
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                            {from !== "/dashboard" && (
                                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Chip
                                        icon={<LinkIcon fontSize="small" />}
                                        label={`Redirection vers: ${from}`}
                                        color="info"
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            maxWidth: '100%',
                                            '.MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'block'
                                            }
                                        }}
                                    />
                                </Box>
                            )}

                            {isSignUp && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label={t.firstName}
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            inputRef={firstNameInputRef}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            required
                                            autoFocus
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label={t.lastName}
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            inputRef={lastNameInputRef}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            <TextField
                                label={t.email}
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                inputRef={emailInputRef}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                required
                                autoFocus={!isSignUp}
                            />
                            <TextField
                                label={t.password}
                                name="password"
                                type={passwordVisible ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                inputRef={passwordInputRef}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                required
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={togglePasswordVisibility} edge="end">
                                                {passwordVisible ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {isSignUp && (
                                <Box sx={{ mt: 1 }}>
                                    <PasswordStrengthBar password={formData.password} />
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                        {t.passwordRecommendations}
                                    </Typography>
                                </Box>
                            )}
                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                sx={{ mt: 3, mb: 2, py: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 450, color:"black" }}

                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : isSignUp ? t.signIn : t.signUp}
                            </Button>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                                <Button onClick={toggleForm} color="primary" sx={{ textTransform: 'none', fontWeight: 450 }}>
                                    {isSignUp ? t.alreadySignedConnexion : t.noAccountSignIn}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Fade>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} elevation={6} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Authorisation;